import { MutableRefObject } from "react";
import { Animated } from "react-native";
import { useLayoutProvider } from "../../../modules/point-of-sale/utils/layout";
import {
  DraggingComponent,
  DropZone,
  Point,
} from "../../../utilities/drag-drop";
import {
  getPageNumberByPosition,
  PageItemType,
  UseAnimatorReturn,
} from "../../../utilities/layout";
import { CollisionType } from "./types";
import { getCollisionResults } from "./utils";

type UseHandleDropArgs = {
  dropZonesRef: MutableRefObject<Map<string, DropZone>>;
  draggingComponentRef: MutableRefObject<DraggingComponent | null>;
  animationController: UseAnimatorReturn;
  pointerRef: MutableRefObject<Animated.ValueXY>;
  offsetRef: MutableRefObject<Point | null>;
  enteredZoneRef: MutableRefObject<string | null>;
  onDragEnd: () => void;
};

export const useDrop = ({
  pointerRef,
  dropZonesRef,
  draggingComponentRef,
  animationController,
  enteredZoneRef,
  onDragEnd,
}: UseHandleDropArgs) => {
  const { pagesController } = useLayoutProvider();

  function handleDrop(droppedItem: PageItemType) {
    const point = JSON.parse(JSON.stringify(pointerRef.current));

    const offsetPosition = {
      x: point.x,
      y: point.y,
    };

    const collisions = getCollisionResults(
      [...dropZonesRef.current.values()],
      offsetPosition
    ).filter(({ hit }) => hit);

    const firstTileCollision = collisions.find(
      (c) => c.type === CollisionType.Tile
    );

    if (!firstTileCollision) {
      const { pageChanged } = cancelDrop(droppedItem);
      if (pageChanged) return;
    }

    return animationController.homing(
      droppedItem.id,
      Number.parseInt(firstTileCollision?.zone.id || `${droppedItem.position}`),
      () => {
        collisions.forEach(({ zone }) => {
          zone.onDrop && zone.onDrop(draggingComponentRef.current?.data);
        });

        resetDropState();
        onDragEnd?.();
      }
    );
  }

  function cancelDrop(droppedItem: PageItemType) {
    const droppedPage = pagesController.pages
      .flatMap((p) => p.items)
      .find((item) => item.id === droppedItem.id);

    if (droppedPage && pagesController.layoutDimensions) {
      const droppedPageNumber = getPageNumberByPosition(
        pagesController.layoutDimensions,
        droppedPage.position
      );

      if (droppedPageNumber !== pagesController.currentPage) {
        const anim = animationController.animationRegistry.get(droppedItem.id);
        anim?.flattenOffset();
        anim?.setValue({ x: 0, y: 0 });
        resetDropState();

        requestAnimationFrame(() => {
          onDragEnd();
          pagesController.goToPage(droppedPageNumber);
        });

        return {
          pageChanged: true,
        };
      }
    }

    return {
      pageChanged: false,
    };
  }

  function resetDropState() {
    draggingComponentRef.current = null;
    enteredZoneRef.current = null;
  }

  return {
    handleDrop,
  };
};
